import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { useLocation } from '@/contexts/LocationContext';
import { useCenterData } from '@/hooks/useCenterData';
import { 
  MapPin,
  Phone,
  Mail,
  Clock,
  Calendar,
  Building2,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Users,
  Star
} from 'lucide-react';

const Contact = () => {
  const navigate = useNavigate();
  const { selectedState, currentStateConfig, setSelectedState } = useLocation();
  const [selectedCenterId, setSelectedCenterId] = useState<string>('');
  
  // Fetch center data from database for selected center (only when one is chosen)
  const { data: centerData } = useCenterData(selectedCenterId || '');

  // Ensure a state is selected (defaults to NSW from LocationContext)
  useEffect(() => {
    if (!selectedState) {
      setSelectedState('NSW');
    }
  }, [selectedState, setSelectedState]);

  // Redirect to franchise page if state has no centers
  useEffect(() => {
    if (selectedState && currentStateConfig && currentStateConfig.centers.length === 0) {
      navigate('/franchise');
    }
  }, [selectedState, currentStateConfig, navigate]);

  // Clear centre selection if it no longer exists in the current state (e.g. after state change
  // or after a centre is closed). User must explicitly choose a centre - no auto-default.
  useEffect(() => {
    if (selectedCenterId && currentStateConfig) {
      const centerExists = currentStateConfig.centers.find(c => c.id === selectedCenterId);
      if (!centerExists) {
        setSelectedCenterId('');
      }
    }
  }, [selectedState, currentStateConfig, selectedCenterId]);

  // Get the selected center - null until the user explicitly picks one
  const selectedCenter = selectedCenterId
    ? currentStateConfig?.centers?.find(c => c.id === selectedCenterId) || null
    : null;


  const handleCenterChange = (centerId: string) => {
    setSelectedCenterId(centerId);
  };

  // Get operating hours from database
  const getCenterOperatingHours = (centerId: string) => {
    // Return hours from database if available, otherwise return default
    if (centerData && centerData.slug === centerId) {
      return centerData.operating_hours || ['TBD - Opening Soon'];
    }
    return ['TBD - Opening Soon'];
  };

  const states = [
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NSW', name: 'New South Wales' },
    { code: 'NT', name: 'Northern Territory' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'WA', name: 'Western Australia' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary/20 to-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-accent/20 to-accent/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-r from-primary/10 to-primary-600/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <Header />
      
      {/* Hero Section with Enhanced Glassmorphism */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-lg border border-white/30 rounded-full px-6 py-3 shadow-2xl">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              <span className="text-primary font-semibold">Contact Schoolola</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-heading font-bold bg-gradient-to-r from-gray-900 via-primary to-primary-600 bg-clip-text text-transparent leading-tight">
              Let's Start Your Child's
              <span className="block">Learning Journey</span>
            </h1>
            
            {/* Enhanced Location Display */}
            {selectedState && currentStateConfig && selectedCenter && (
              <div className="bg-white/30 backdrop-blur-xl border border-white/40 rounded-3xl p-8 inline-block shadow-2xl hover:shadow-3xl transition-all duration-500 hover:bg-white/40">
                <div className="flex items-center justify-center space-x-4 text-lg">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-gray-700 font-medium">
                    <span className="block text-sm text-gray-600">Currently viewing</span>
                    <span className="font-bold">{selectedCenter.name} Centre, {currentStateConfig.name}</span>
                  </div>
                </div>
              </div>
            )}
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Ready to unlock your child's potential? Our expert educators are here to guide you through our courses and answer all your questions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      {selectedState && currentStateConfig && currentStateConfig.centers.length > 0 ? (
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              
              {/* Enhanced State and Centre Selector */}
              <div className="mb-12 flex flex-col lg:flex-row gap-6 items-center justify-center">
                <Card className="bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:bg-white/40">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-r from-primary to-primary-600 rounded-full flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-lg font-semibold text-gray-700">Select State</Label>
                        <Select value={selectedState} onValueChange={setSelectedState}>
                          <SelectTrigger className="w-64 bg-white/50 backdrop-blur-sm border border-white/40 shadow-lg hover:bg-white/60 transition-all">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl">
                            {states.map((state) => (
                              <SelectItem key={state.code} value={state.code} className="hover:bg-primary/10 transition-all duration-200">
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:bg-white/40">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-r from-primary to-primary-600 rounded-full flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-lg font-semibold text-gray-700">Select Centre</Label>
                        <Select value={selectedCenterId} onValueChange={handleCenterChange}>
                          <SelectTrigger className="w-56 bg-white/50 backdrop-blur-sm border border-white/40 shadow-lg hover:bg-white/60 transition-all">
                            <SelectValue placeholder="Select a centre" />
                          </SelectTrigger>
                          <SelectContent className="bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl">
                            {currentStateConfig.centers.map((center) => (
                              <SelectItem key={center.id} value={center.id} className="hover:bg-primary/10 transition-all duration-200">
                                {center.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Centre Information - only shown once a centre is explicitly selected */}
              {selectedCenter ? (
              <div className="w-full space-y-12">

                {/* Enhanced Centre Header */}
                <Card className="bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500">
                  <CardContent className="p-12 text-center">
                    <div className="space-y-6">
                      <div className="h-20 w-20 bg-gradient-to-r from-primary to-primary-600 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                        <Building2 className="h-10 w-10 text-white" />
                      </div>
                      <div>
                        <h2 className="text-4xl font-heading font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent mb-3">
                          {selectedCenter.name} Centre
                        </h2>
                        <p className="text-xl text-gray-600 mb-4">
                          Your local Schoolola tutoring centre in {currentStateConfig.name}
                        </p>
                        <Badge className={selectedCenterId === 'caroline-springs' 
                          ? "bg-gradient-to-r from-green-500 to-green-600 text-white border-0 shadow-lg px-4 py-2 text-sm"
                          : "bg-gradient-to-r from-green-500 to-green-600 text-white border-0 shadow-lg px-4 py-2 text-sm"}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {selectedCenterId === 'caroline-springs' || selectedCenterId === 'truganina'
                            ? 'Enrolments are now open'
                            : ['colebee', 'the-ponds', 'riverstone'].includes(selectedCenterId) 
                              ? 'Enrolments open' 
                              : 'Opening to enrolments soon'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Enhanced Contact Cards Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <Card className="group bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 hover:bg-white/40 h-full flex flex-col">
                    <CardHeader className="text-center pb-4">
                      <div className="h-16 w-16 bg-gradient-to-r from-primary to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300">
                        <Phone className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-800">Call Us</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center flex-1 flex flex-col justify-between">
                      {selectedCenter.phone ? (
                        <>
                          <a 
                            href={`tel:${selectedCenter.phone}`} 
                            className="text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent hover:from-primary/80 hover:to-primary-600/80 transition-all duration-300 block mb-4"
                          >
                            {selectedCenter.phone}
                          </a>
                          <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-primary-600 hover:from-primary/90 hover:to-primary-600/90 shadow-xl text-white border-0 mt-auto">
                            <a href={`tel:${selectedCenter.phone}`}>
                              <Phone className="h-4 w-4 mr-2" />
                              Call Now
                            </a>
                          </Button>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-muted-foreground block mb-4">TBD</span>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="group bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 hover:bg-white/40 h-full flex flex-col">
                    <CardHeader className="text-center pb-4">
                      <div className="h-16 w-16 bg-gradient-to-r from-accent to-accent/80 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300">
                        <Mail className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-800">Email Us</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center flex-1 flex flex-col justify-between">
                      <p className="text-sm text-gray-600 font-medium break-all mb-4">
                        {selectedCenter.email}
                      </p>
                      <Button 
                        asChild 
                        variant="outline" 
                        size="lg" 
                        className="w-full bg-white/50 backdrop-blur-sm border-2 border-accent/30 shadow-xl hover:bg-white/70 hover:border-accent/50 transition-all mt-auto"
                      >
                        <a href={`mailto:${selectedCenter.email}`}>
                          <Mail className="h-4 w-4 mr-2" />
                          Send Email
                        </a>
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="group bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 hover:bg-white/40 h-full flex flex-col">
                    <CardHeader className="text-center pb-4">
                      <div className="h-16 w-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300">
                        <MapPin className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-800">Visit Us</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center flex-1 flex flex-col justify-between">
                      {selectedCenterId === 'truganina' ? (
                        <div className="text-sm text-gray-600 leading-relaxed mb-4 space-y-3">
                          <div>
                            <p className="font-semibold text-gray-700">Mainview Boulevard Family Learning Centre <span className="text-green-600 text-xs">(NEW)</span></p>
                            <p>49 Mainview Blvd | Truganina VIC 3029</p>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-700">Koomail Tardy Community Centre</p>
                            <p>117 Alcock Rd | Truganina VIC 3029</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">
                          {selectedCenter.address}
                        </p>
                      )}
                      <Button 
                        variant="outline" 
                        size="lg" 
                        className="w-full bg-white/50 backdrop-blur-sm border-2 border-green-200 shadow-xl hover:bg-white/70 hover:border-green-300 transition-all mt-auto"
                        onClick={() => {
                          const searchQuery = encodeURIComponent(`Schoolola ${selectedCenter.address}`);
                          window.open(`https://www.google.com/maps/search/?api=1&query=${searchQuery}`, '_blank');
                        }}
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        Get Directions
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="group bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 hover:bg-white/40">
                    <CardHeader className="text-center pb-4">
                      <div className="h-16 w-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300">
                        <Clock className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-800">Hours</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                        <div className="space-y-1 text-sm">
                          {getCenterOperatingHours(selectedCenter.id).map((hours, index) => (
                            <div key={index} className="font-medium text-gray-700">{hours}</div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                   </div>

                {/* Enhanced Quick Actions */}
                <Card className="bg-gradient-to-br from-white/40 to-white/20 backdrop-blur-xl border border-white/40 shadow-2xl">
                  <CardHeader>
                    <CardTitle className="text-center text-2xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent flex items-center justify-center">
                      <Star className="h-6 w-6 text-yellow-500 mr-2" />
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {selectedCenter.id === 'truganina' ? (
                        <Button 
                          size="lg" 
                          disabled
                          className="h-14 bg-gray-400 text-gray-600 shadow-xl border-0 font-semibold cursor-not-allowed"
                        >
                          <Calendar className="mr-2 h-5 w-5" />
                          Book Free Assessment
                        </Button>
                      ) : (
                        <Button asChild size="lg" className="h-14 bg-gradient-to-r from-accent to-accent/90 hover:from-accent/90 hover:to-accent/80 shadow-xl text-white border-0 font-semibold">
                          <a href="/book-assessment">
                            <Calendar className="mr-2 h-5 w-5" />
                            Book Free Assessment
                          </a>
                        </Button>
                      )}
                      <Button asChild variant="outline" size="lg" className="h-14 bg-white/50 backdrop-blur-sm border-2 border-primary/20 shadow-xl hover:bg-white/70 hover:border-primary/30 transition-all font-semibold">
                        <a href="/programs">
                          <Users className="mr-2 h-5 w-5" />
                          Explore Programs
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Enhanced Other Centres with Accordion */}
                {currentStateConfig.centers.length > 1 && (
                  <Card className="bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl">
                    <CardHeader>
                      <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                        <Building2 className="mr-3 h-6 w-6 text-primary" />
                        Other Centres in {currentStateConfig.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible className="w-full">
                        {currentStateConfig.centers
                          .filter(center => center.id !== selectedCenter.id)
                          .map((center) => (
                          <AccordionItem key={center.id} value={center.id} className="border-b border-white/20">
                            <AccordionTrigger className="hover:no-underline hover:bg-white/20 px-4 py-4 rounded-lg transition-all">
                              <div className="flex items-center space-x-3">
                                <Building2 className="h-5 w-5 text-primary" />
                                <span className="font-semibold text-gray-800">{center.name}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <Card className="bg-white/40 backdrop-blur-lg border border-white/50 shadow-xl">
                                <CardContent className="p-6">
                                  <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-bold text-lg text-gray-800">{center.name}</h4>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleCenterChange(center.id)}
                                      className="bg-white/60 backdrop-blur-sm border border-white/60 shadow-lg hover:bg-white/80 transition-all"
                                    >
                                      Switch
                                      <ArrowRight className="h-3 w-3 ml-1" />
                                    </Button>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{center.address}</p>
                                  <div className="space-y-2 text-sm">
                                    <a href={`tel:${center.phone}`} className="flex items-center text-primary hover:text-primary/80 font-medium transition-colors">
                                      <Phone className="h-4 w-4 mr-2" />
                                      {center.phone}
                                    </a>
                                    <a href={`mailto:${center.email}`} className="flex items-center text-accent hover:text-accent/80 font-medium transition-colors break-all">
                                      <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                                      {center.email}
                                    </a>
                                  </div>
                                </CardContent>
                              </Card>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                )}
              </div>
              ) : (
                <Card className="bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl">
                  <CardContent className="p-12 text-center space-y-3">
                    <Building2 className="h-10 w-10 text-primary mx-auto" />
                    <h2 className="text-2xl font-bold text-gray-800">Select a centre to continue</h2>
                    <p className="text-gray-600">Choose a centre above to see its phone, email, address and opening hours.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20 relative">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto space-y-8">
              <h2 className="text-3xl font-bold text-gray-800">Loading Contact Information...</h2>
              <p className="text-gray-600">Please wait while we load your centre details.</p>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Contact;